/**
 * FE#257 — one source of truth for the lazy-loading list's sort order.
 *
 * `LazyLoadingVirtuoso` needs the SAME mongo-style sort object for two things:
 *   1. the client-side DISPLAY (`selectByQuery(..., sortObj)` re-sorts the whole
 *      loaded set for the screen), and
 *   2. the paginated FETCH (`$sort` on every `DBThunks.find`, page after page).
 *
 * They used to be built by two separate inline blocks that drifted apart: the
 * display object carried a deterministic `_id: -1` tiebreak, the fetch object
 * did not. With FE#157's reconciled `$skip`, the loaded set after N pages is
 * exactly the first `N * pageSize` rows in FETCH order — so if the fetch order
 * disagrees with the display order (e.g. only the fetch lacks the tiebreak, or
 * they use different fields), the loaded window is NOT a prefix of the displayed
 * order and the visible top of the list is wrong until more pages load. On tied
 * primary values (campaigns bulk-created in one drip share an `updatedAt`) the
 * un-tiebroken fetch order is not even stable across pages.
 *
 * Building the object here once, and using it on BOTH sides, keeps the loaded
 * window a true prefix of what the screen shows.
 */

export interface LazyLoadSortOptions {
  field: string;
  order: 'asc' | 'desc';
  statusFilter?: string | null;
}

// Map a UI sort-field name to its actual database field path.
export function getSortFieldPath(field: string): string {
  const fieldMap: Record<string, string> = {
    dueDate: 'schedule.dueDate',
  };
  return fieldMap[field] ?? field;
}

/**
 * Build the mongo-style sort object for the lazy-loading list. Always includes a
 * deterministic `_id: -1` secondary so equal primary values order the same way
 * on the display side and the paginated fetch side (and page boundaries stay
 * stable). Pass the SAME return value to `selectByQuery` and to the find `$sort`.
 */
export function buildLazyLoadSort(
  sortOptions?: LazyLoadSortOptions | null,
): Record<string, 1 | -1> {
  // Sorting by a specific status yields all-equal primary values, so order by
  // the send/scheduled time instead (matches the display's secondary intent).
  const effectiveField =
    sortOptions?.field === 'campaignStatus' && sortOptions?.statusFilter
      ? 'schedule.dueDate'
      : sortOptions
        ? getSortFieldPath(sortOptions.field)
        : 'updatedAt';
  const order: 1 | -1 = sortOptions ? (sortOptions.order === 'asc' ? 1 : -1) : -1;
  return {
    [effectiveField]: order,
    _id: -1, // deterministic tiebreak — identical on display + fetch
  };
}
